import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Stack,
    Chip,
    TextField,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import RewardBadge from '../../widgets/RewardBadge';

import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import NewContentDialog from '../../widgets/contents/NewContentDialog';
import ContentDialog from '../../widgets/contents/ContentDialog';
import ContentList from '../../widgets/contents/ContentList';
import ContentDeleteDialog from '../../widgets/contents/ContentDeleteDialog';

const ContentsPage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [groupId, setGroupId] = useState('');
    const [gameId, setGameId] = useState('');
    const [type, setType] = useState('');
    const [groups, setGroups] = useState([]);
    const [games, setGames] = useState([]);

    useEffect(() => {
        fetchContents();
    }, []);

    const fetchContents = async () => {
        try {
            setLoading(true);
            const data = await RequestService.getContents(
                user.company.id,
                searchTerm,
                groupId,
                gameId,
                type
            );
            setContents(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Erro ao carregar conteúdos.');
            setContents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const groupData = await RequestService.getGroups(
                    user.company.id
                );
                setGroups(Array.isArray(groupData) ? groupData : []);

                const gameData = await RequestService.getGames(user.company.id);
                setGames(Array.isArray(gameData) ? gameData : []);
            } catch (err) {
                console.error('Erro ao carregar filtros:', err);
            }
        };

        fetchFilters();
    }, [user.company.id]);

    const handleRowClick = (player) => {
        if (hasPermission('CONTENT_UPDATE')) {
            setSelectedPlayer(player);
            setIsDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedPlayer(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => {
        setIsAddDialogOpen(false);
    };

    const handleOpenDeleteDialog = (content) => {
        setSelectedPlayer(content);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedPlayer(null);
    };

    const handleSuccessDelete = async () => {
        const data = await RequestService.getContents(user.company.id);
        setContents(data);
        setSnackbarMessage('Conteúdo excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessAdd = async () => {
        const data = await RequestService.getContents(user.company.id);
        setContents(data);

        setSnackbarMessage('Conteúdo criado com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const columns = [
        {
            label: 'Título e Descrição',
            field: 'title',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#292929' }}>
                        {row.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        {row.description}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Jogos',
            field: 'games',
            align: 'center',
            renderCell: (value, row) => (
                <ChipGroup items={row.games} labelKey="name" />
            ),
        },
        {
            label: 'Turma',
            field: 'groups',
            align: 'center',
            renderCell: (value, row) => (
                <ChipGroup items={row.groups} labelKey="name" />
            ),
        },
        {
            label: 'Recompensa',
            field: 'reward',
            align: 'center',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={2} alignItems="center">
                    <RewardBadge
                        icon={
                            <CoinIcon
                                style={{ color: '#134F99', fontSize: '24px' }}
                            />
                        }
                        value={row.coin}
                        positive={row.attitudeType !== 'NEGATIVE'}
                    />
                    <RewardBadge
                        icon={
                            <XpIcon
                                style={{ color: '#134F99', fontSize: '24px' }}
                            />
                        }
                        value={row.exp}
                        positive={row.attitudeType !== 'NEGATIVE'}
                    />
                </Stack>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction="row" spacing={2}>
                    {hasPermission('CONTENT_UPDATE') && (
                        <Link
                            component="button"
                            underline="none"
                            onClick={() => handleRowClick(row)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'blue',
                            }}
                        >
                            <EditIcon sx={{ color: '#1F83FF' }} />
                            <Typography
                                variant="body2"
                                sx={{ ml: 0.5, color: '#1F83FF' }}
                            >
                                Editar
                            </Typography>
                        </Link>
                    )}
                    {hasPermission('CONTENT_DELETE') && (
                        <Link
                            component="button"
                            underline="none"
                            onClick={() => handleOpenDeleteDialog(row)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'red',
                            }}
                        >
                            <DeleteOutlinedIcon fontSize="small" />
                            <Typography variant="body2" sx={{ ml: 0.5 }}>
                                Excluir
                            </Typography>
                        </Link>
                    )}
                </Stack>
            ),
        },
    ];

    const renderEmptyState = () => (
        <Box sx={{ textAlign: 'center', mt: 4, color: '#666' }}>
            <Typography variant="h6">Nenhum conteúdo cadastrado.</Typography>
            <Typography variant="body2">
                Clique em <strong>"Nova Missão"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () =>
        contents.length === 0 ? (
            renderEmptyState()
        ) : (
            <ContentList
                contents={contents}
                shouldEdit={hasPermission('CONTENT_UPDATE')}
                shouldDelete={hasPermission('CONTENT_DELETE')}
                onUserClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );

    const renderDesktopLayout = () => {
        return contents.length === 0 ? (
            renderEmptyState()
        ) : (
            <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                <TableComponent columns={columns} rows={contents} />
            </Box>
        );
    };

    const ChipGroup = ({ items, labelKey }) => {
        if (!items || items.length === 0) return null;

        const visibleItems = items.slice(0, 1);
        const remainingCount = items.length - 1;

        return (
            <Stack direction="row" spacing={1}>
                {visibleItems.map((item) => (
                    <Chip
                        key={item.id}
                        label={item[labelKey]}
                        size="small"
                        color="primary"
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip
                        label={`+${remainingCount}`}
                        size="small"
                        color="default"
                    />
                )}
            </Stack>
        );
    };

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Missões
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Gerenciar
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Missões
                </Typography>
            </Grid>

            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                wrap="wrap"
            >
                {/* Left side: filters */}
                <Grid container item xs={12} md={9} spacing={2}>
                    {/* Search */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            id="search"
                            label="Pesquisar Nome"
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    fetchContents();
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    {/* Grupo */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl sx={{ minWidth: '200px' }} fullWidth>
                            <InputLabel>Grupo</InputLabel>
                            <Select
                                value={groupId}
                                onChange={(e) => setGroupId(e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {groups.map((group) => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Jogo */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl sx={{ minWidth: '200px' }} fullWidth>
                            <InputLabel>Jogo</InputLabel>
                            <Select
                                value={gameId}
                                onChange={(e) => setGameId(e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {games.map((game) => (
                                    <MenuItem key={game.id} value={game.id}>
                                        {game.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Tipo */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl sx={{ minWidth: '200px' }} fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="MISSION">Missão</MenuItem>
                                <MenuItem value="ATTITUDE">Atitude</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Aplicar Filtros */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            sx={{ minHeight: '47px' }}
                            onClick={fetchContents}
                            fullWidth
                        >
                            Aplicar Filtros
                        </Button>
                    </Grid>
                </Grid>

                {/* Right side: “Nova Missão” button */}
                <Grid item xs={12} md="auto">
                    {hasPermission('CONTENT_CREATE') && (
                        <Button
                            variant="contained"
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                            sx={{ minHeight: '47px', whiteSpace: 'nowrap' }}
                        >
                            Nova Missão
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <NewContentDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <ContentDialog
                open={isDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDialog();
                    }
                }}
                content={selectedPlayer}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <ContentDeleteDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    }
                }}
                content={selectedPlayer}
                onSuccess={handleSuccessDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ContentsPage;
